import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getEtsyOrder } from '@/lib/etsy/orders'

function formatMoney(currency: string | null | undefined, amount: number | null | undefined): string {
  if (typeof amount !== 'number') return '—'
  return `${String(currency || 'USD')} ${amount.toFixed(2)}`
}

function formatCreated(createdAt: unknown): string {
  if (typeof createdAt === 'number') {
    const d = new Date(createdAt * 1000)
    if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 16).replace('T', ' ')
  }
  if (typeof createdAt === 'string' && createdAt) return createdAt
  return '—'
}

export default function EtsyOrderDetail() {
  const { brandId, shopId, receiptId } = useParams<{
    brandId: string
    shopId: string
    receiptId: string
  }>()

  const listHref =
    brandId && shopId ? `/brands/${brandId}/integrations/etsy/orders?shopId=${encodeURIComponent(shopId)}` : '/brands/picker'

  const { data, isLoading, error } = useQuery({
    queryKey: ['etsy', 'order', brandId, shopId, receiptId],
    queryFn: () => getEtsyOrder(brandId!, shopId!, receiptId!),
    enabled: !!brandId && !!shopId && !!receiptId,
  })

  const order = data?.order
  const items = data?.items ?? []

  const displayTotal = useMemo(() => formatMoney(order?.currency, order?.total), [order?.currency, order?.total])

  if (!brandId) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24">
        <header className="sticky top-0 bg-white border-b border-gray-200 z-10 p-4">
          <h1 className="text-xl font-bold text-gray-900">Etsy order</h1>
        </header>
        <main className="p-4">
          <p className="text-sm text-gray-600 mb-3">Choose a brand to view Etsy orders.</p>
          <Link to="/brands/picker" className="text-blue-600 font-medium underline">
            Pick a brand
          </Link>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="p-4 flex items-start gap-2">
          <Link
            to={listHref}
            className="shrink-0 w-11 h-11 flex items-center justify-center rounded-lg text-sky-700 hover:bg-sky-50 text-xl"
            aria-label="Back to Etsy orders"
          >
            ←
          </Link>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900">Etsy order</h1>
            <p className="text-xs text-gray-500 mt-1">Receipt {receiptId}</p>
          </div>
        </div>
      </header>

      <main className="p-4 space-y-4">
        {isLoading ? <p className="text-sm text-gray-500">Loading order…</p> : null}
        {error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-900">
            {(error as Error).message}
          </div>
        ) : null}

        {order ? (
          <>
            <section className="bg-white rounded-lg p-4 border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Summary</h2>
              <div className="grid grid-cols-1 gap-3 text-sm">
                <div>
                  <div className="text-[11px] text-gray-500 uppercase tracking-wide">Receipt id</div>
                  <div className="font-mono">{order.receiptId}</div>
                </div>
                <div>
                  <div className="text-[11px] text-gray-500 uppercase tracking-wide">Status</div>
                  <div className="font-medium">{order.status || '—'}</div>
                </div>
                <div>
                  <div className="text-[11px] text-gray-500 uppercase tracking-wide">Buyer</div>
                  <div className="font-medium">{order.buyerName || '—'}</div>
                </div>
                <div>
                  <div className="text-[11px] text-gray-500 uppercase tracking-wide">Created</div>
                  <div className="font-medium">{formatCreated(order.createdAt)}</div>
                </div>
                <div>
                  <div className="text-[11px] text-gray-500 uppercase tracking-wide">Total</div>
                  <div className="font-mono">{displayTotal}</div>
                </div>
              </div>
            </section>

            <section className="bg-white rounded-lg p-4 border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Items</h2>
              {items.length === 0 ? (
                <p className="text-sm text-gray-600">No items found.</p>
              ) : (
                <div className="space-y-3">
                  {items.map((it, idx) => (
                    <div key={`${it.title}-${idx}`} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                      <div className="font-semibold text-gray-900">{it.title}</div>
                      <div className="mt-2 grid grid-cols-2 gap-3 text-xs text-gray-700">
                        <div>
                          <div className="text-[10px] text-gray-500 uppercase tracking-wide">Qty</div>
                          <div className="font-mono">{it.quantity ?? '—'}</div>
                        </div>
                        <div>
                          <div className="text-[10px] text-gray-500 uppercase tracking-wide">Price</div>
                          <div className="font-mono">{formatMoney(it.currency || order.currency, it.price)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        ) : null}
      </main>
    </div>
  )
}

